import React, { useCallback, useEffect, useState } from 'react';
import { normalizeLanguageCode } from './translationHelpers';
import { logger } from '../../utils/logger';

interface TranslationObserverProps {
  isTranslated: boolean;
  isTranslating: boolean;
  isApplyingTranslations: boolean;
  currentLanguage: string;
  ignoreProcessedNodes: boolean;
  processedNodes: WeakSet<Node>;
  shouldTranslateNode: (node: Node, targetLanguage: string) => boolean;
  translateAddedNodes: (nodes: Node[], forcedLanguage?: string) => Promise<void>;
  translateModalContent: () => Promise<void>;
  translationLock: string | null;
  setNodesBeingTranslated: React.Dispatch<React.SetStateAction<number>>;
}

export const useTranslationObserver = ({
  isTranslated,
  isTranslating,
  isApplyingTranslations,
  currentLanguage,
  ignoreProcessedNodes,
  processedNodes,
  shouldTranslateNode,
  translateAddedNodes,
  setNodesBeingTranslated
}: TranslationObserverProps) => {
  const [observer, setObserver] = useState<MutationObserver | null>(null);
  
  // Function to stop observing DOM changes
  const stopObserving = useCallback(() => {
    if (observer) {
      observer.disconnect();
      setObserver(null);
    }
  }, [observer]);
  
  // Function to start observing DOM changes
  const startObserving = useCallback((forcedLanguage?: string) => {
    // If we already have an observer, disconnect it first to avoid duplicates
    if (observer) {
      observer.disconnect();
      setObserver(null);
    }
    
    // Create a set to track nodes that need translation to avoid duplicates
    const pendingNodes = new Set<Node>();
    
    // CRITICAL: Use the provided language or capture the current language at the time the observer is created
    // This will be the single source of truth for this observer
    // Store the language in a closure to ensure it doesn't change during the observer's lifetime
    const observerTargetLanguage = forcedLanguage || normalizeLanguageCode(currentLanguage);
    
    // CRITICAL: Check if the language from localStorage is different from what we're using
    // This ensures consistency across page navigation
    try {
      const savedLanguage = localStorage.getItem('currentLanguage');
      if (savedLanguage && normalizeLanguageCode(savedLanguage) !== observerTargetLanguage) {
        // Use the language from localStorage as the source of truth
        const normalizedSavedLanguage = normalizeLanguageCode(savedLanguage);
        return startObserving(normalizedSavedLanguage);
      }
    } catch (error) {
      logger.error('Error checking language from localStorage in observer:', error);
    }
    
    const newObserver = new MutationObserver((mutations) => {
      // Only process translation mutations if we're not currently applying translations
      if (isApplyingTranslations) {
        return;
      }
      
      let hasChanges = false;
      
      mutations.forEach(mutation => {
        // Skip mutations that are likely caused by our own translation process
        if (mutation.target.nodeType === Node.ELEMENT_NODE) {
          const element = mutation.target as Element;
          const nodeLanguage = element.getAttribute('data-translated-lang');
          
          // Only skip if the element is already translated to the target language
          if (nodeLanguage === observerTargetLanguage) {
            return;
          }
        }
        // Handle added nodes
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // If an element was added, get all text nodes inside it
              try {
                // Process all element nodes - we'll find text nodes inside them
                const walker = document.createTreeWalker(
                  node,
                  NodeFilter.SHOW_TEXT,
                  {
                    acceptNode: (textNode) => {
                      // Use our shared helper function to check if this node should be translated
                      return shouldTranslateNode(textNode, observerTargetLanguage) 
                        ? NodeFilter.FILTER_ACCEPT 
                        : NodeFilter.FILTER_REJECT;
                    }
                  }
                );
                
                let textNode;
                while (textNode = walker.nextNode()) {
                  // CRITICAL: Add original language and text attributes to the node's parent
                  // This ensures that all nodes have the necessary attributes before translation
                  if (textNode.parentElement && textNode.textContent && !textNode.parentElement.hasAttribute('data-original-text')) {
                    try {
                      textNode.parentElement.setAttribute('data-original-text', textNode.textContent);
                      textNode.parentElement.setAttribute('data-original-lang', 'EN');
                    } catch (e) {
                      logger.error('Could not set data attribute:', e);
                    }
                  }
                  
                  pendingNodes.add(textNode);
                  hasChanges = true;
                }
              } catch (error) {
                logger.error('Error walking text nodes:', error);
              }
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
              // Skip already processed nodes (unless we're ignoring processed nodes)
              if (!ignoreProcessedNodes && processedNodes.has(node)) {
                return;
              }
              
              // Check if this text node should be translated
              const parent = node.parentElement;
              if (parent) {
                const tagName = parent.tagName.toLowerCase();
                if (!['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName) &&
                    !parent.closest('.translation-badge') && 
                    !parent.closest('.translation-controls')) {
                  
                  // CRITICAL: Add original language and text attributes to the node's parent
                  // This ensures that all nodes have the necessary attributes before translation
                  if (node.textContent && !parent.hasAttribute('data-original-text')) {
                    try {
                      parent.setAttribute('data-original-text', node.textContent);
                      parent.setAttribute('data-original-lang', 'EN');
                    } catch (e) {
                      logger.error('Could not set data attribute:', e);
                    }
                  }
                  
                  pendingNodes.add(node);
                  hasChanges = true;
                }
              }
            }
          });
        }
        
        // Handle text content changes
        if (mutation.type === 'characterData' && mutation.target.nodeType === Node.TEXT_NODE) {
          const textNode = mutation.target;
          
          // Skip already processed nodes (unless we're ignoring processed nodes)
          if (!ignoreProcessedNodes && processedNodes.has(textNode)) {
            return;
          }
          
          if (textNode.textContent?.trim()) {
            const parent = textNode.parentElement;
            if (parent) {
              const tagName = parent.tagName.toLowerCase();
              if (!['script', 'style', 'noscript', 'option', 'code', 'pre'].includes(tagName) &&
                  !parent.closest('.translation-badge') && 
                  !parent.closest('.translation-controls')) {
                
                // CRITICAL: Add original language and text attributes to the node's parent
                // This ensures that all nodes have the necessary attributes before translation
                if (textNode.textContent && !parent.hasAttribute('data-original-text')) {
                  try {
                    parent.setAttribute('data-original-text', textNode.textContent);
                    parent.setAttribute('data-original-lang', 'EN');
                  } catch (e) {
                    logger.error('Could not set data attribute:', e);
                  }
                }
                
                pendingNodes.add(textNode);
                hasChanges = true;
              }
            }
          }
        }
      });
      
      // If there are changes, schedule a translation
      if (hasChanges) {
        // CRITICAL: Only increment nodesBeingTranslated if translation is enabled AND language is not English
        // This prevents the preloader from showing when translation is disabled
        const isEnglish = normalizeLanguageCode(currentLanguage) === 'EN';
        
        if (isTranslated && !isEnglish) {
          // Update the nodesBeingTranslated counter
          setNodesBeingTranslated(prev => prev + pendingNodes.size);
          
          // Create a function to translate the nodes and decrement the counter
          const translateAndDecrement = async () => {
            try {
              await translateAddedNodes(Array.from(pendingNodes), observerTargetLanguage);
            } finally {
              // Decrement the counter regardless of success or failure
              setNodesBeingTranslated(prev => Math.max(0, prev - pendingNodes.size));
              pendingNodes.clear();
            }
          };
          
          // Debounce the translation and decrement function
          setTimeout(translateAndDecrement, 100);
        } else {
          // If translation is disabled or language is English, just call translateAddedNodes
          // without incrementing nodesBeingTranslated
          setTimeout(() => {
            translateAddedNodes(Array.from(pendingNodes), observerTargetLanguage);
            pendingNodes.clear();
          }, 100);
        }
      }
    });
    
    // Start observing the entire document with expanded options
    newObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true, // Also observe text content changes
      attributes: true // Watch for all attribute changes
    });
    
    setObserver(newObserver);
    
    // Also observe the head for dynamically added stylesheets or scripts
    // that might affect content visibility
    try {
      newObserver.observe(document.head, {
        childList: true,
        subtree: true
      });
    } catch (error) {
      logger.error('Error observing document head:', error);
    }
    
    return newObserver;
  }, [
    observer, 
    currentLanguage,
    shouldTranslateNode, 
    ignoreProcessedNodes, 
    processedNodes, 
    translateAddedNodes,
    isApplyingTranslations,
    isTranslated,
    setNodesBeingTranslated
  ]);
  
  // Clean up observer on unmount
  useEffect(() => {
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [observer]);
  
  // Disable automatic presentation mode detection since we're using explicit type props now
  
  return {
    observer,
    startObserving,
    stopObserving
  };
};
